// src/components/admin/compensation/EmployeeCard.js
import React from 'react';
import { Mail, MapPin, Calendar, Eye, Edit, MoreVertical, TrendingUp, TrendingDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const EmployeeCard = ({ employee, department, onViewDetails, viewMode = 'grid' }) => {
    const navigate = useNavigate();

    // Helper to get CTC from employee object (checks multiple possible field names)
    const getEmployeeCTC = (emp) => {
        return emp.annual_ctc ||
               emp.ctc ||
               emp.pay_package?.annual_ctc ||
               emp.pay_package?.ctc ||
               0;
    };

    // Helper to get initials from name
    const getInitials = (name) => {
        if (!name) return 'NA';
        return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (amount >= 10000000) {
            return `₹${(amount / 10000000).toFixed(2)}Cr`;
        } else if (amount >= 100000) {
            return `₹${(amount / 100000).toFixed(2)}L`;
        } else {
            return `₹${amount.toLocaleString('en-IN')}`;
        }
    };

    // Get status badge color
    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
            case 'inactive':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
            case 'on_leave':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
        }
    };

    // Format date in IST
    const formatDate = (dateString) => {
        if (!dateString) return 'Not Specified';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
    };

    const annualCTC = getEmployeeCTC(employee);
    const monthlyCTC = annualCTC / 12;

    // Grid view card
    if (viewMode === 'grid') {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow duration-200 overflow-hidden">
                {/* Card Header */}
                <div className="p-6 pb-4">
                    <div className="flex items-start gap-4">
                        {/* Avatar */}
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-lg">{getInitials(employee.name)}</span>
                        </div>

                        {/* Employee Info */}
                        <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                                {employee.name || 'Not Specified'}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                {employee.designation || 'Not Specified'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                {employee.employee_code}
                            </p>
                        </div>

                        {/* Status Badge */}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(employee.employment_status)}`}>
                            {employee.employment_status === 'on_leave' ? 'On Leave' : employee.employment_status || 'Active'}
                        </span>
                    </div>
                </div>

                {/* Department and Location */}
                <div className="px-6 pb-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <MapPin className="w-4 h-4" />
                        <span>{department?.name || 'Not Specified'}</span>
                    </div>
                    {employee.work_location && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <MapPin className="w-4 h-4" />
                            <span>{employee.work_location}</span>
                        </div>
                    )}
                </div>

                {/* Compensation Details */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Annual CTC</span>
                        <span className="text-xs text-gray-500 dark:text-gray-500">Monthly: {formatCurrency(monthlyCTC)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {formatCurrency(annualCTC)}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 flex gap-2">
                    <button
                        onClick={onViewDetails}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                    >
                        <Eye className="w-4 h-4" />
                        View Details
                    </button>
                    <button
                        onClick={() => navigate(`/admin/employees/${employee.id}?tab=job`)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center justify-center"
                    >
                        <Edit className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }

    // List view card
    if (viewMode === 'list') {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-shadow duration-200 overflow-hidden">
                <div className="p-6">
                    <div className="flex items-center gap-6">
                        {/* Avatar */}
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-lg">{getInitials(employee.name)}</span>
                        </div>

                        {/* Employee Info */}
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {employee.name || 'Not Specified'}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {employee.designation || 'Not Specified'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    {employee.employee_code}
                                </p>
                            </div>

                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-1">Department</p>
                                <p className="text-sm text-gray-900 dark:text-white font-medium">
                                    {department?.name || 'Not Specified'}
                                </p>
                                {employee.work_location && (
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {employee.work_location}
                                    </p>
                                )}
                            </div>

                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-1">Annual CTC</p>
                                <p className="text-xl font-bold text-green-600 dark:text-green-400">
                                    {formatCurrency(annualCTC)}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    ₹{Math.round(monthlyCTC).toLocaleString('en-IN')}/month
                                </p>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={onViewDetails}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                                >
                                    <Eye className="w-4 h-4" />
                                    View
                                </button>
                                <button
                                    onClick={() => navigate(`/admin/employees/${employee.id}?tab=job`)}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                >
                                    <Edit className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default EmployeeCard;
